import {
  IKindredCouponSummary,
  IKindredDealSummary,
  IKindredOfferSummary,
  KindredPublishStatus,
} from "../../interfaces";
import { EntityStatus } from "./types";

const PUBLISHED_LABEL: Record<number, string> = {
  [KindredPublishStatus.Draft]: "Draft",
  [KindredPublishStatus.AwaitingApproval]: "Awaiting approval",
  [KindredPublishStatus.Approved]: "Approved",
  [KindredPublishStatus.Deactivated]: "Deactivated",
};

const cell = (v: unknown) =>
  `"${String(v ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/"/g, '""')}"`;

const download = (filename: string, header: string[], rows: unknown[][]) => {
  const csv = [header, ...rows].map((r) => r.map(cell).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename;
  a.click();
  // Revoked a beat later: some browsers cancel the download if it goes at once.
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

const published = (n: number) => PUBLISHED_LABEL[n] ?? String(n);

/** e.g. "lowes.com-US-coupons-2026-10-16.csv" */
export const exportFilename = (
  kind: string,
  domain?: string | null,
  country?: string | null
) =>
  [domain, country, kind, new Date().toISOString().slice(0, 10)]
    .filter(Boolean)
    .join("-") + ".csv";

/** Every coupon passed in, whatever the status filter shows. */
export const downloadCouponsCsv = (
  coupons: Array<IKindredCouponSummary & { _status: EntityStatus }>,
  filename: string
) =>
  download(
    filename,
    [
      "coupon_id",
      "code",
      "brand",
      "brand_id",
      "valid_from",
      "valid_to",
      "current_status",
      "published_status",
      "code_domains",
      "deal_domains",
      "countries",
      "source",
      "priority",
      "deactivated_at",
      "summary",
    ],
    coupons.map((c) => [
      c.id,
      c.code,
      c.brandName,
      c.brandId,
      c.validFrom,
      c.validTo,
      c._status,
      published(c.publishedStatus),
      c.couponDomains.join(" "),
      c.campaignDomains.join(" "),
      c.countries.join(" "),
      c.source,
      c.priority,
      c.deactivatedAt,
      c.summary,
    ])
  );

export const downloadDealsCsv = (
  deals: Array<IKindredDealSummary & { _status: EntityStatus }>,
  filename: string
) =>
  download(
    filename,
    [
      "deal_id",
      "name",
      "brand",
      "brand_id",
      "start_date",
      "end_date",
      "current_status",
      "published_status",
      "suspended",
      "deal_domains",
      "countries",
      "source",
      "merchant_id",
    ],
    deals.map((d) => [
      d.id,
      d.name,
      d.brandName,
      d.brandId,
      d.startDate,
      d.endDate,
      d._status,
      published(d.publishedStatus),
      d.suspended ? "yes" : "no",
      d.domains.join(" "),
      d.countries.join(" "),
      d.source,
      d.merchantId,
    ])
  );

export const downloadOffersCsv = (
  offers: Array<IKindredOfferSummary & { _status: EntityStatus }>,
  filename: string
) =>
  download(
    filename,
    [
      "offer_id",
      "summary",
      "brand",
      "brand_id",
      "valid_from",
      "valid_to",
      "current_status",
      "published_status",
      "offer_domains",
      "deal_domains",
      "countries",
      "source",
      "priority",
      "deactivated_at",
    ],
    offers.map((o) => [
      o.id,
      o.summary,
      o.brandName,
      o.brandId,
      o.validFrom,
      o.validTo,
      o._status,
      published(o.publishedStatus),
      o.offerDomains.join(" "),
      o.campaignDomains.join(" "),
      o.countries.join(" "),
      o.source,
      o.priority,
      o.deactivatedAt,
    ])
  );
